import { Injector, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TourTooltipComponent } from '../components/tour-tooltip/tour-tooltip.component';
import { TranslateService } from '@ngx-translate/core';
import { WordDetailsModalComponent } from '../components/word-details-modal/word-details-modal.component';
import { ModalController } from '@ionic/angular';
import { UserService } from './user.service';
import { AnalyticsService } from './gtag.service';
@Injectable({ providedIn: 'root' })
export class TourService {

	private steps: { route: string; selector: string; content: string, custom_coords?: { left: string; top: string }, delay?: boolean, scroll?: boolean }[] = [];

	public currentStepIndex = 0;
	public get totalSteps() {
		return this.steps.length;
	}
	private overlayRef: any;

	private targetLang: string = 'en'; // Default language

	private beginModalTour: string = '#word-details-modal-root';
	private beforeModalTour: string = '#example_word';
	private afterModalTour: string = '#study-page-button';
	private endModalTour: string = '#study-button';

	private screenChange: boolean = false; // Flag to track screen size changes

	constructor(private router: Router, private overlay: Overlay, 
		private injector: Injector, private translateService: TranslateService,
		private modalController: ModalController, private userService: UserService, private gtagService: AnalyticsService) {}

	public async startTour(tourLang: string = 'en') {
		this.gtagService.trackEvent('tour', 'start', tourLang);
		this.userService.touring = true; // Set touring flag to true
		this.targetLang = tourLang; // Set the target language for the tour
		this.steps = [
			{ route: '/news', selector: '#home-page-button', 
				content: this.translateService.instant('TOUR.HOME_PAGE'),
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '400px' } } : {}),
				delay: true 
			},
			// { route: '/news', selector: '#topic-bar', content: this.translateService.instant('TOUR.TOPIC_SELECTION') },
			/* { route: '/news', selector: '#preview-block-0', 
				content: this.translateService.instant('TOUR.TOPIC_ARTICLES'),
			...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '479px' } } : {}), scroll: true },*/
			/*{ route: '/article/'+tourLang, selector: '#difficulty_panel', content: this.translateService.instant('TOUR.DIFFICULTY'), delay: true,
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '150px' } } : {})
			 },*/
			{ route: '/article/'+tourLang, selector: '#example_word', content: this.translateService.instant('TOUR.EXAMPLE_WORD'), scroll: true, 
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '150px' } } : { custom_coords: { left: '30%', top: '450px' } }), delay: true  
			}, 
			{ route: '/article/'+tourLang, 
				selector: '#word-details-modal-root', 
				content: this.translateService.instant('TOUR.DETAILS_CARD'),
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '150px' } } : { custom_coords: { left: '30%', top: '450px' } }), delay: true }, /*
			{ route: 'article/'+tourLang, selector: '#study-button', content: this.translateService.instant('TOUR.STUDY_BUTTON') },
			{
				route: '/article/' + tourLang,
				selector: '#study-page-button',
				content: this.translateService.instant('TOUR.STUDY_MENU_BUTTON'),
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '450px' } } : {})
			},*/
			{ route: '/study/', selector: '#study-page-button', content: this.translateService.instant('TOUR.STUDY_PAGE'), ...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '400px' } } : {}) },
			/*{ route: '/study/', selector: '#start-study-session', content: this.translateService.instant('TOUR.START_STUDYING'), 
				delay: true, ...(this.isMobileScreen() ? { custom_coords: { left: '10px', top: '350px' } } : {}) },
			{ route: '/study/'+tourLang, selector: '#flip-button', content: this.translateService.instant('TOUR.STUDY_FLIP'), delay: true },
			{ route: '/study/'+tourLang, selector: '#answer-buttons', content: this.translateService.instant('TOUR.ANSWER_BUTTONS'),
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '245px' } } : {}),
				delay: true,
				scroll: (this.isMobileScreen() ? true : false)
			},*/ 
			{ route: '/study/', selector: '#home-page-button', 
				content: this.translateService.instant('TOUR.FINISH_TOUR'),
				...(this.isMobileScreen() ? { custom_coords: { left: '0px', top: '450px' } } : {}),
			},

			//{ route: '/study/', selector: '#study-page-button', content: this.translateService.instant('TOUR.STUDY_BUTTON') },
		];
		this.currentStepIndex = 0;

		await this.showStep(this.currentStepIndex);
	}

	private async showStep(index: number) {
		const step = this.steps[index];
		if (!step) return;

		await this.router.navigate([step.route], { replaceUrl: true });
		
		// Mobile devices need more time for navigation and rendering
		const isMobile = this.isMobileScreen();
		const baseWaitTime = isMobile ? 800 : 600; // Longer wait for mobile
		
		if (this.screenChange || step.delay) {
			await new Promise(resolve => setTimeout(resolve, baseWaitTime));
		}

		// Special handling for example_word on mobile
		const isExampleWord = step.selector === '#example_word';
		const maxAttempts = isExampleWord && isMobile ? 20 : (isExampleWord ? 15 : 10);
		const baseDelay = isExampleWord && isMobile ? 400 : (isExampleWord ? 300 : 200);

		// For article page elements, we need to wait longer and retry element detection
		const element = await this.waitForElement(step.selector, maxAttempts, baseDelay);
		if (!element) {
			console.warn(`Tour element not found: ${step.selector}`);
			return;
		}

		if (step.scroll) {
			// On mobile, use different scroll behavior and longer wait times
			if (isMobile) {
				// Force iOS to scroll by using both scrollIntoView and manual scroll
				element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
				await new Promise(resolve => setTimeout(resolve, 500));
				
				// Additional iOS-specific scroll attempt
				const rect = element.getBoundingClientRect();
				const scrollTop = window.pageYOffset + rect.top - (window.innerHeight / 2);
				window.scrollTo({ top: scrollTop, behavior: 'smooth' });
				await new Promise(resolve => setTimeout(resolve, 750)); // Longer wait for mobile
			} else {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' });
				await new Promise(resolve => setTimeout(resolve, 500));
			}
		}

		const rect = element.getBoundingClientRect();
		const highlightStyle = {
			left: `${rect.left - 4}px`,
			top: `${rect.top - 4}px`,
			width: `${rect.width + 8}px`,
			height: `${rect.height + 8}px`
		};

		let tooltipTop = rect.bottom + 12;
		if (tooltipTop + 80 > window.innerHeight) {
			tooltipTop = rect.top - 80;
		}
		const tooltipStyle = {
			left: `${rect.left}px`,
			top: `${tooltipTop}px`
		};

		if (step.custom_coords) {
			tooltipStyle.left = step.custom_coords.left;
			tooltipStyle.top = step.custom_coords.top;
		}

		if (this.overlayRef) {
			this.overlayRef.dispose();
		}
		this.overlayRef = this.overlay.create({
			hasBackdrop: false,
			scrollStrategy: this.overlay.scrollStrategies.block()
		});

		const tooltipPortal = new ComponentPortal(TourTooltipComponent, null, this.injector);
		const tooltipRef = this.overlayRef.attach(tooltipPortal);
		tooltipRef.instance.content = step.content;
		tooltipRef.instance.highlightStyle = highlightStyle;
		tooltipRef.instance.tooltipStyle = tooltipStyle;
		tooltipRef.instance.hasPrev = index > 0;
		tooltipRef.instance.hasNext = index < this.steps.length - 1;
		tooltipRef.instance.prev = () => this.goToPrev();
		tooltipRef.instance.next = () => this.goToNext();
	}

	public async goToPrev() {
		// Hide tooltip immediately for smooth transition
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
		
		let currentRoute = this.steps[this.currentStepIndex].route;
		if (this.currentStepIndex > 0) {
			// Check if the route has changed
			this.checkIfRouteChanged(false);
			this.currentStepIndex--;
			if (this.getCurrentSelector() ===  this.beforeModalTour) {
				// Close the word card modal if we are going back to the second step
				const modals = await this.modalController.getTop();
				if (modals) {
					await modals.dismiss();
				}
			}
			else if (this.getCurrentSelector() === this.endModalTour) {
				// Open the word card modal again
				await this.openWordCard();
			}
			await this.showStep(this.currentStepIndex);
		}
	}

	private checkIfRouteChanged(forward: boolean) {
		let next_index = this.currentStepIndex;
		const currentRoute = this.steps[next_index].route;
		if (forward) {
			next_index += 1;
		}
		else {
			next_index -= 1;
		}
		if (this.steps[next_index].route !== currentRoute) {
			this.screenChange = true; // Set flag if route changes
		}
		else if (this.screenChange) {
			this.screenChange = false; // Reset flag if we are going back to the same route
		}
	}

	public async goToNext() {
		// Hide tooltip immediately for smooth transition
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
		
		if (this.currentStepIndex < this.steps.length - 1) {
			// Check if the route has changed
			this.checkIfRouteChanged(true);
			this.currentStepIndex++;
			if (this.getCurrentSelector() === this.beginModalTour) {
				await this.openWordCard();
			}
			else if (this.getCurrentSelector() === this.afterModalTour) {
				// Close the word card modal if we are going to the study button step
				const modals = await this.modalController.getTop();
				if (modals) {
					await modals.dismiss();
				}
			}
			else if(this.getCurrentSelector() === '#answer-buttons') {
			// Flip the card if we are on the answer buttons step
				this.flipCard();
			}
			await this.showStep(this.currentStepIndex);
		}
		else {
			// End of tour
			this.finishTour();
		}
	}

	public async finishTour() {
		this.gtagService.trackEvent('tour', 'end', this.targetLang);
		if (this.overlayRef) {
			this.overlayRef.dispose();
		}
		document.body.style.overflow = ''; // Reset body overflow
		this.currentStepIndex = 0; // Reset step index
		this.steps = []; // Clear steps

		// Close modal if open
		const modal = await this.modalController.getTop();
		if (modal) {
			await modal.dismiss();
		}

		this.userService.updateUserTouringStatus().then(() => {
			this.router.navigate(['/playlists'], { replaceUrl: true });
		}).catch(err => {
			console.error('Error updating user touring status:', err);
		});
	}

	private getCurrentSelector(): string {
		if (this.currentStepIndex < this.steps.length) {
			return this.steps[this.currentStepIndex].selector;
		}
		return '';
	}

	public async openWordCard() {
	  const modal = await this.modalController.create({
		component: WordDetailsModalComponent,
		componentProps: {
			word: 'EZ Knews',
			contextSentence: '',
			targetLanguage: this.targetLang,
			nativeLanguage: '',
			proficiencyLevel: 0,
			articleId: ''
			},
			breakpoints: [0, 0.5, 0.9],
			initialBreakpoint: 0.5,
			handle: true,
			cssClass: 'word-sheet-modal'
		});
		await modal.present();
	}

	private getVisibleElement(selector: string): HTMLElement | null {
		const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
		
		// On mobile, be more lenient with visibility checks
		const isMobile = this.isMobileScreen();
		
		return elements.find(el => {
			const style = window.getComputedStyle(el);
			const rect = el.getBoundingClientRect();
			
			// Basic visibility checks
			const isDisplayed = style.display !== 'none';
			const isVisible = style.visibility !== 'hidden';
			
			if (isMobile) {
				// On mobile, also check if element has some dimensions and is potentially in viewport
				const hasSize = rect.width > 0 && rect.height > 0;
				const isInDocument = document.contains(el);
				return isDisplayed && isVisible && hasSize && isInDocument;
			} else {
				// Standard desktop checks
				return isDisplayed && isVisible && el.offsetParent !== null;
			}
		}) || null;
	}

	private async waitForElement(selector: string, maxAttempts: number = 10, baseDelay: number = 200): Promise<HTMLElement | null> {
		const isMobile = this.isMobileScreen();
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			const element = this.getVisibleElement(selector);
			if (element) {
				// On iOS, double-check the element is truly ready
				if (isIOS && selector === '#example_word') {
					const rect = element.getBoundingClientRect();
					if (rect.width === 0 || rect.height === 0) {
						console.log(`iOS: Element ${selector} found but has no dimensions, retrying...`);
						const delay = baseDelay * Math.pow(1.5, attempt);
						await new Promise(resolve => setTimeout(resolve, delay));
						continue;
					}
				}
				return element;
			}
			
			if (isMobile) {
				console.log(`Mobile: Attempt ${attempt + 1}/${maxAttempts} to find ${selector}`);
			}
			
			// Exponential backoff: wait longer each time
			const delay = baseDelay * Math.pow(1.5, attempt);
			await new Promise(resolve => setTimeout(resolve, delay));
		}
		
		console.warn(`Element ${selector} not found after ${maxAttempts} attempts on ${isMobile ? 'mobile' : 'desktop'}`);
		return null;
	}

	private isMobileScreen(): boolean {
		// Check for mobile device types and screen size
		const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
		const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
		
		// Return true if either mobile device or small screen
		return isMobileDevice || isSmallScreen;
	}

	private flipCard() {
		// Logic to flip the card, if needed
		const flipButton = document.getElementById('flip-button');
		if (flipButton) {
			flipButton.click(); // Simulate a click to flip the card
		}
	}
}